/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import type {
  EditorJsonContent,
  FaceAsset,
  FadeDirection,
  FalconContent,
  ImageSrcSet,
  LogoShape,
  TileAsset,
  TileFragment,
  TilesContent,
} from '../contentTypes';
import { emptyLogo } from './testContent';
import { tileModelToNemo } from './tileModelToNemo';
import { percentageToDecimal } from './utils';

/**
 * Checks if content has flat personalizationFeaturedTile_* fields
 */
const hasFlatFeaturedTileFields = (fields: Record<string, any>): boolean => {
  return Object.keys(fields).some((key) => key.startsWith('personalizationFeaturedTile_'));
};

/**
 * Converts flat personalizationFeaturedTile_* fields into a nested TileFragment structure
 * that tileModelToNemo can process.
 */
const reconstructFeaturedTileFromFlatFields = (
  fields: Record<string, any>,
): { tile: TileFragment; isEnabled: boolean | undefined } => {
  // Extract all personalizationFeaturedTile_* fields
  const prefix = 'personalizationFeaturedTile_';
  const flatFields: Record<string, any> = {};

  Object.keys(fields).forEach((key) => {
    if (key.startsWith(prefix)) {
      const fieldName = key.slice(prefix.length);
      flatFields[fieldName] = fields[key];
    }
  });

  // Reconstruct the nested structure expected by tileModelToNemo
  // Note: mediaAsset must be explicitly null (not undefined) or have a valid structure
  // to avoid crashes in getMediaAssetContent
  const tile = {
    __type: 'aem-headless',
    contentPath: '/personalized-featured-tile',
    path: '/personalized-featured-tile',
    fields: {
      theme: flatFields.theme,
      icon: flatFields.icon,
      mediaAsset: flatFields.mediaAsset !== undefined ? flatFields.mediaAsset : null,

      logo_alt: flatFields.logo_alt,
      logo_srcAsset: flatFields.logo_srcAsset,

      logo_heights_xs: flatFields.logo_heights_xs,
      logo_heights_s: flatFields.logo_heights_s,
      logo_heights_m: flatFields.logo_heights_m,
      logo_heights_l: flatFields.logo_heights_l,
      logo_heights_xl: flatFields.logo_heights_xl,
      logo_heights_xxl: flatFields.logo_heights_xxl,

      darkHeadingsEnabled: flatFields.darkHeadingsEnabled,

      heading_text: flatFields.heading_text,
      heading_variant: flatFields.heading_variant,
      heading_htmlTag: flatFields.heading_htmlTag,
      heading_embolden: flatFields.heading_embolden,

      subHeading_text: flatFields.subHeading_text,
      subHeading_variant: flatFields.subHeading_variant,
      subHeading_htmlTag: flatFields.subHeading_htmlTag,
      subHeading_embolden: flatFields.subHeading_embolden,

      CTA_href: flatFields.CTA_href,
      CTA_variant: flatFields.CTA_variant,
      CTA_text: flatFields.CTA_text,
      CTA_ariaLabel: flatFields.CTA_ariaLabel,
      CTA_linkType: flatFields.CTA_linkType,
      CTA_openIn: flatFields.CTA_openIn,

      horizontalAlignment: flatFields.horizontalAlignment,
      verticalAlignment: flatFields.verticalAlignment,

      wltpKeys: flatFields.wltpKeys,
      disclaimers: flatFields.disclaimers,

      fadeEnabled: flatFields.fadeEnabled,
      fade_color: flatFields.fade_color,
      fade_opacity: flatFields.fade_opacity,
      fade_height: flatFields.fade_height,
      fade_direction: flatFields.fade_direction,

      zoomEnabled: flatFields.zoomEnabled,
    },
  } as TileFragment;

  return {
    tile,
    isEnabled: flatFields.isEnabled,
  };
};

/**
 * Normalizes FalconContent by converting flat personalizationFeaturedTile_* fields
 * into a nested personalizationFeaturedTile object if needed.
 * This ensures compatibility with code that expects the nested structure.
 */
export const normalizeFalconContent = (content: FalconContent): FalconContent => {
  if (!content.fields || content.fields.personalizationFeaturedTile) {
    // Already has nested structure or no fields
    return content;
  }

  if (!hasFlatFeaturedTileFields(content.fields)) {
    // No flat fields present
    return content;
  }

  // Reconstruct nested structure from flat fields
  const { tile, isEnabled } = reconstructFeaturedTileFromFlatFields(content.fields);

  // Create personalizationFeaturedTile with isEnabled at root level

  const personalizationFeaturedTile: any = {
    ...tile,
    fields: {
      ...tile.fields,
      isEnabled,
    },
  };

  return {
    ...content,
    fields: {
      ...content.fields,
      personalizationFeaturedTile,
    },
  };
};

export const mapHeadlessContent = (content: FalconContent): TilesContent => {
  const {
    layout_name: layoutName, // 🔥 [E]
    layout_size: layoutSize, // 🔥 [E]
    tiles,
    personalizationData, // 🔥 [E]
    personalizationFeaturedTile, // 🔥 [E]
  } = content.fields;

  const pd = personalizationData ? (JSON.parse(personalizationData) as TilesContent) : undefined; // 🔥 [E]

  // Use personalizationData tiles if available, otherwise process AEM tiles // 🔥 [E]
  const processedTiles = pd?.tiles || tiles.map((tile) => tileModelToNemo(tile)); // 🔥 [E]

  // Handle featured tile - support both nested and flat field structures
  let featuredTile;
  if (personalizationFeaturedTile) {
    // Nested structure (existing behavior)
    featuredTile = {
      ...tileModelToNemo(personalizationFeaturedTile),
      isEnabled: personalizationFeaturedTile.fields?.isEnabled,
    };
  } else if (hasFlatFeaturedTileFields(content.fields)) {
    // Flat structure (new behavior for content service)
    const { tile, isEnabled } = reconstructFeaturedTileFromFlatFields(content.fields);
    featuredTile = {
      ...tileModelToNemo(tile),
      isEnabled,
    };
  } else {
    featuredTile = undefined;
  }

  const appContent: TilesContent = {
    layout: {
      name: pd?.layout?.name || layoutName, // 🔥 [E]
      size: pd?.layout?.size || layoutSize, // 🔥 [E]
    },
    tiles: processedTiles, // 🔥 [E]
    personalizationData: pd, // 🔥 [E]
    personalizationFeaturedTile: featuredTile, // 🔥 [E]
  };
  return appContent;
};

const mapAssetContent = (asset: FaceAsset): TileAsset => {
  if (asset?.assetType === 'image') {
    const smallSrcSet = ['md', 'sm', 'xs'].map((bp) => ({
      bp,
      src: asset.imageUrlSmall.assetValue,
      srcAsset: '',
    })) as ImageSrcSet;
    const largeSrcSet = ['xxl', 'xl', 'lg'].map((bp) => ({
      bp,
      src: asset.imageUrlLarge.assetValue,
      srcAsset: '',
    })) as ImageSrcSet;
    return {
      assetType: asset?.assetType,
      imageEnabled: true,
      videoEnabled: false,
      image: {
        alt: asset.alt,
        srcSet: [...largeSrcSet, ...smallSrcSet],
      },
      video: {
        alt: '',
        autoplay: false,
        loop: false,
        alternateDescriptionPortrait: '',
        audioDescription: {
          path: '',
        },
        audioDescriptionPortrait: {
          path: '',
        },
        closedCaption: {
          path: '',
        },
        closedCaptionPortrait: {
          path: '',
        },
        srcs: {
          xs: { src: '', poster: '' },
          s: { src: '', poster: '' },
          m: { src: '', poster: '' },
          l: { src: '', poster: '' },
          xl: { src: '', poster: '' },
          xxl: { src: '', poster: '' },
        },
      },
    };
  }

  const {
    autoplay,
    loop,
    videoUrlSmall,
    videoUrlLarge,
    posterImageUrlSmall,
    posterImageUrlLarge,
    alternateDescriptionPortrait,
    audioDescription,
    audioDescriptionPortrait,
    closedCaption,
    closedCaptionPortrait,
    alt,
    assetType,
  } = asset;

  const smallSrcShape = {
    src: videoUrlSmall.assetValue,
    poster: posterImageUrlSmall.assetValue,
  };
  const largeSrcShape = {
    src: videoUrlLarge.assetValue,
    poster: posterImageUrlLarge.assetValue,
  };
  return {
    assetType,
    imageEnabled: false,
    videoEnabled: true,
    image: {
      alt: '',
      srcSet: [],
    },
    video: {
      alt,
      autoplay,
      loop,
      closedCaptionPortrait,
      closedCaption,
      audioDescription,
      audioDescriptionPortrait,
      alternateDescriptionPortrait,
      srcs: {
        xs: smallSrcShape,
        s: smallSrcShape,
        m: smallSrcShape,
        l: largeSrcShape,
        xl: largeSrcShape,
        xxl: largeSrcShape,
      },
    },
  };
};

export const mapFaceContent = (content: EditorJsonContent): TilesContent => {
  const {
    layout,
    tiles,
    personalizationData, // 🔥 [E]
    personalizationFeaturedTile, // 🔥 [E]
  } = content;
  const newTiles = tiles.map((tile) => {
    const {
      horizontalAlignment,
      verticalAlignment,
      CTA,
      mediaAsset,
      logo,
      disclaimers,
      fadeEnabled,
      fade,
      wltpKeys,
      ...restTile
    } = tile;
    const { linkType: CTAlinkType, openIn, ...restCTA } = CTA;

    const newLogo = (() => {
      if (logo) {
        const { src: logoSrc, srcAsset: logoSrcAsset, ...restLogo } = logo;
        return {
          src: (typeof logoSrcAsset === 'object' && logoSrcAsset.assetValue) || logoSrc || '',
          srcAsset: { assetValue: '' },
          ...restLogo,
        } as LogoShape;
      }
      return emptyLogo;
    })();
    const { newFade } = (() => {
      if (fadeEnabled) {
        return {
          newFade: {
            opacity: percentageToDecimal(fade?.opacity || '0%'),
            color: fade?.color || '',
            direction: fade?.direction || 'bottom',
            height: fade?.height || '',
          },
        };
      }
      return {
        newFade: {
          opacity: '',
          color: '',
          direction: 'bottom' as FadeDirection,
          height: '',
        },
      };
    })();

    return {
      ...restTile,
      logo: newLogo,
      mediaAsset,
      media: {
        ...mapAssetContent(mediaAsset!),
        fadeEnabled,
        fade: newFade,
      },
      CTA: {
        linkType: CTAlinkType,
        openIn,
        ...restCTA,
      },
      disclaimers,
      horizontalAlignment,
      verticalAlignment,
      fade,
      fadeEnabled,
      wltpKeys: wltpKeys || [],
    };
  });

  return {
    layout: personalizationData?.layout || layout, // 🔥 [E]
    tiles: newTiles,
    personalizationData: personalizationData as TilesContent, // 🔥 [E]
    personalizationFeaturedTile, // 🔥 [E]
  };
};
